import { Link } from '@tanstack/react-router';
import { Check, ExternalLink, Smartphone } from 'lucide-react';
import { jobSiteLine } from '../../lib/job-site.js';
import { Feedback } from '../ui/feedback.js';
import { cn } from '../../lib/cn.js';
import {
  durationLabel,
  hourLabel,
  readCompletedAt,
  useDispatchBoard,
  type Technician,
} from '../../lib/dispatch-board.js';
import { tradeLabel, type Trade } from '../../lib/trade.js';
import { TradePill } from '../ui/trade-pill.js';
import type { SalesOrderSummary, SalesType } from '../../lib/api.js';

interface MobileDispatchProps {
  salesTypes: ReadonlyArray<SalesType>;
  techs: ReadonlyArray<Technician>;
  queue: ReadonlyArray<{ order: SalesOrderSummary; trade: Trade }>;
  placed: ReadonlyArray<{
    order: SalesOrderSummary;
    trade: Trade;
    techId: string;
    date: string;
    startHour: number;
    duration: number;
    /** Set for an extra visit — it has no Unschedule here (edit it on a larger screen). */
    visitId?: string;
  }>;
  onUnassign: (orderId: number) => void;
}

/**
 * Mobile dispatch — read-friendly summary of who's assigned to what.
 *
 * Drag-and-drop on a 5×8 timeline is impractical at 375px width; we hide
 * the desktop BoardGrid below md and surface this layout instead:
 *
 *   - Unassigned queue at the top (filtered + sorted upstream)
 *   - One section per tech with their assignments listed by start hour
 *   - Tap a placed job → unschedule action
 *
 * Re-assigning to a different tech still happens on desktop. This view
 * is intended for at-a-glance "who has what today" checks from a phone,
 * not for scheduling.
 */
export function MobileDispatch({ salesTypes, techs, queue, placed, onUnassign }: MobileDispatchProps) {
  const filter = useDispatchBoard((s) => s.filter);
  const selectedDate = useDispatchBoard((s) => s.selectedDate);
  const dayLabel = formatDayHeading(selectedDate);

  // The queue prop arrives UNfiltered so the header can show the true
  // backlog; the visible list applies the trade filter locally.
  const visibleQueue = filter === 'all' ? queue : queue.filter((q) => q.trade === filter);

  const placedByTech = new Map<string, MobileDispatchProps['placed'][number][]>();
  for (const tech of techs) placedByTech.set(tech.id, []);
  for (const p of placed) {
    if (filter !== 'all' && p.trade !== filter) continue;
    const bucket = placedByTech.get(p.techId);
    if (bucket) bucket.push(p);
  }
  // Sort each tech's list by start hour ascending.
  for (const bucket of placedByTech.values()) {
    bucket.sort((a, b) => a.startHour - b.startHour);
  }

  return (
    <div className="md:hidden p-3 space-y-4">
      <Feedback variant="info" size="sm">
        <div className="flex items-center gap-1.5 text-[11px]">
          <Smartphone className="h-3.5 w-3.5" aria-hidden />
          Drag-and-drop dispatch requires a larger screen. This phone view is read-only —
          tap a job to unschedule.
        </div>
      </Feedback>

      <section>
        <h3 className="text-[10px] uppercase tracking-wider text-ink-subtle mb-1.5">
          Unassigned ({queue.length})
          {visibleQueue.length !== queue.length && (
            <span className="normal-case tracking-normal"> · {visibleQueue.length} shown</span>
          )}
        </h3>
        {visibleQueue.length === 0 ? (
          <div
            className="rounded-md border bg-surface p-3 text-xs text-ink-muted"
            style={{ borderColor: 'var(--color-border-tertiary)' }}
          >
            {queue.length === 0
              ? 'Nothing in the queue.'
              : 'Nothing in the queue for this trade filter.'}
          </div>
        ) : (
          <ul className="space-y-2">
            {visibleQueue.map(({ order, trade }) => (
              <li key={order.spireId}>
                <UnassignedCard salesTypes={salesTypes} order={order} trade={trade} />
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="space-y-3">
        <h3 className="text-[10px] uppercase tracking-wider text-ink-subtle">
          Assignments for {dayLabel}
        </h3>
        {techs.map((tech) => {
          const list = placedByTech.get(tech.id) ?? [];
          return (
            <TechBlock salesTypes={salesTypes} key={tech.id} tech={tech} placed={list} onUnassign={onUnassign} />
          );
        })}
      </section>
    </div>
  );
}

function UnassignedCard({
  salesTypes,
  order,
  trade,
}: {
  salesTypes: ReadonlyArray<SalesType>;
  order: SalesOrderSummary;
  trade: Trade;
}) {
  return (
    <Link
      to="/sales-orders/$id"
      params={{ id: String(order.spireId) }}
      className="block rounded-md border bg-surface px-3 py-2 transition-shadow hover:shadow-sm"
      style={{ borderColor: 'var(--color-border-tertiary)' }}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="text-sm font-medium text-ink truncate">{order.customer.name}</div>
          {jobSiteLine(order) && (
            <div className="text-[11px] text-ink-muted truncate">{jobSiteLine(order)}</div>
          )}
          <div className="text-[11px] font-mono text-ink-muted">
            {order.orderNo} · ${order.total}
          </div>
        </div>
        <TradePill types={salesTypes} trade={trade} />
      </div>
    </Link>
  );
}

function TechBlock({
  salesTypes,
  tech,
  placed,
  onUnassign,
}: {
  salesTypes: ReadonlyArray<SalesType>;
  tech: Technician;
  placed: ReadonlyArray<{
    order: SalesOrderSummary;
    trade: Trade;
    techId: string;
    date: string;
    startHour: number;
    duration: number;
    /** Set for an extra visit — it has no Unschedule here (edit it on a larger screen). */
    visitId?: string;
  }>;
  onUnassign: (orderId: number) => void;
}) {
  const swatch = AVATAR_SWATCH[tech.avatar];
  return (
    <div
      className="rounded-md border bg-surface"
      style={{ borderColor: 'var(--color-border-tertiary)' }}
    >
      <header className="flex items-center gap-2 px-3 py-2 border-b" style={{ borderColor: 'var(--color-border-tertiary)' }}>
        <span
          aria-hidden
          className="inline-flex h-7 w-7 items-center justify-center rounded-full text-[10px] font-semibold"
          style={{ backgroundColor: swatch.bg, color: swatch.fg }}
        >
          {tech.initials}
        </span>
        <div className="min-w-0 flex-1">
          <div className="text-sm font-medium text-ink truncate">{tech.name}</div>
          <div className="text-[10px] text-ink-muted truncate">
            {tech.active ? tech.trades.map((t) => tradeLabel(salesTypes, t)).join(' · ') : 'Inactive — reassign'}
          </div>
        </div>
        <span className="text-[10px] text-ink-subtle">
          {placed.length === 0 ? 'No jobs' : `${placed.length} job${placed.length === 1 ? '' : 's'}`}
        </span>
      </header>
      {placed.length === 0 ? (
        <div className="px-3 py-3 text-xs text-ink-subtle">Unscheduled for this day.</div>
      ) : (
        <ul>
          {placed.map((p) => (
            <li key={`${p.order.spireId}:${p.visitId ?? 'first'}`} className="border-t" style={{ borderColor: 'var(--color-border-tertiary)' }}>
              <PlacedCard salesTypes={salesTypes} placed={p} onUnassign={onUnassign} />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function PlacedCard({
  salesTypes,
  placed,
  onUnassign,
}: {
  salesTypes: ReadonlyArray<SalesType>;
  placed: {
    order: SalesOrderSummary;
    trade: Trade;
    techId: string;
    date: string;
    startHour: number;
    duration: number;
    /** Set for an extra visit — it has no Unschedule here (edit it on a larger screen). */
    visitId?: string;
  };
  onUnassign: (orderId: number) => void;
}) {
  const completed = readCompletedAt(placed.order.udf ?? {});
  return (
    <div className={cn('px-3 py-2.5', completed && 'opacity-65')}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="flex items-center gap-1.5">
            {completed && <Check className="h-3.5 w-3.5 text-emerald-700 shrink-0" aria-label="Completed" />}
            <span className={cn('text-sm font-medium text-ink truncate', completed && 'line-through')}>
              {placed.order.customer.name}
            </span>
          </div>
          {jobSiteLine(placed.order) && (
            <div className="text-[11px] text-ink-muted truncate">{jobSiteLine(placed.order)}</div>
          )}
          <div className="text-[11px] font-mono text-ink-muted">
            {placed.order.orderNo} · ${placed.order.total}
            {placed.visitId && <span className="font-sans"> · extra visit</span>}
          </div>
          <div className="text-[11px] text-ink-muted mt-0.5">
            {hourLabel(placed.startHour)}–{hourLabel(placed.startHour + placed.duration)} ({durationLabel(placed.duration)})
            {completed && (
              <> · done {completed.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })}</>
            )}
          </div>
        </div>
        <TradePill types={salesTypes} trade={placed.trade} />
      </div>
      <div className="mt-2 flex items-center gap-3 justify-end">
        <Link
          to="/sales-orders/$id"
          params={{ id: String(placed.order.spireId) }}
          className="inline-flex items-center gap-1 text-xs text-ink-accent hover:underline"
        >
          <ExternalLink className="h-3 w-3" aria-hidden />
          Open
        </Link>
        {!placed.visitId && (
          <button
            type="button"
            onClick={() => onUnassign(placed.order.spireId)}
            className="text-xs text-ink-muted hover:text-red-700 hover:underline"
          >
            Unschedule
          </button>
        )}
      </div>
    </div>
  );
}

const AVATAR_SWATCH: Record<Technician['avatar'], { bg: string; fg: string }> = {
  install: { bg: '#CECBF6', fg: '#26215C' },
  service: { bg: '#B5D4F4', fg: '#042C53' },
  pm: { bg: '#9FE1CB', fg: '#04342C' },
  emergency: { bg: '#F7C1C1', fg: '#501313' },
  sales: { bg: '#FAC775', fg: '#412402' },
};

function formatDayHeading(iso: string): string {
  const [y, m, d] = iso.split('-').map(Number);
  const dt = new Date(y!, (m ?? 1) - 1, d ?? 1);
  return dt.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' });
}
